"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildAddPermissionlessDelegatorTransaction = exports.buildAddPermissionlessValidatorTransaction = exports.buildTransformSupernetTransaction = exports.buildTransferSupernetOwnershipTransaction = exports.buildRemoveSupernetValidatorTransaction = exports.buildCreateChainTransaction = exports.buildCreateSupernetTransaction = exports.buildAddSupernetValidatorTransaction = exports.buildPlatformImportTransaction = exports.buildPlatformExportTransaction = exports.buildPlatformBaseTransaction = void 0;
const utils_1 = require("../../utils");
const builder_1 = require("../builder");
const input_1 = require("../input");
const output_1 = require("../output");
const transaction_1 = require("../transaction");
const types_1 = require("../types");
const supernet_1 = require("./supernet");
const transaction_2 = require("./transaction");
function buildPlatformBaseTransaction(userInputs, utxoSet, signersAddresses, fee, changeAddress, networkId, memo = '') {
    if (userInputs.length < 1) {
        throw new utils_1.InputError('user inputs cannot be empty');
    }
    const sourceId = userInputs[0].sourceChain.id;
    for (const input of userInputs) {
        if (input.sourceChain.id !== sourceId || input.destinationChain.id !== sourceId) {
            throw new utils_1.InputError('jvm base transaction cannot have different source/destination chain user inputs');
        }
    }
    const feeData = new transaction_1.TransactionFee(userInputs[0].sourceChain, fee);
    const inputs = (0, builder_1.buildTransactionInputs)(userInputs, utxoSet, signersAddresses, [feeData]);
    const outputs = (0, builder_1.buildTransactionOutputs)(userInputs, inputs, feeData, changeAddress);
    return new transaction_2.PlatformBaseTransaction(networkId, new types_1.BlockchainId(sourceId), outputs, inputs, memo);
}
exports.buildPlatformBaseTransaction = buildPlatformBaseTransaction;
function buildPlatformExportTransaction(userInputs, utxoSet, signersAddresses, exportAddress, sourceFee, destinationFee, changeAddress, networkId, memo = '') {
    if (userInputs.length < 1) {
        throw new utils_1.InputError('user inputs cannot be empty');
    }
    const sourceId = userInputs[0].sourceChain.id;
    const destinationId = userInputs[0].destinationChain.id;
    for (const input of userInputs) {
        if (input.sourceChain.id !== sourceId || input.destinationChain.id !== destinationId) {
            throw new utils_1.InputError('jvm export transaction cannot have different source or destination chain user inputs');
        }
        if (input.sourceChain.id === input.destinationChain.id) {
            throw new utils_1.InputError('jvm export transaction cannot have the same chain as source and destination user inputs');
        }
    }
    const sourceFeeData = new transaction_1.TransactionFee(userInputs[0].sourceChain, sourceFee);
    const destinationFeeData = new transaction_1.TransactionFee(userInputs[0].destinationChain, destinationFee);
    const fees = [sourceFeeData, destinationFeeData];
    const inputs = (0, builder_1.buildTransactionInputs)(userInputs, utxoSet, signersAddresses, fees);
    // fixed user inputs with a defined export address to import it later
    const fixedUserInputs = [];
    for (const input of userInputs) {
        fixedUserInputs.push(new input_1.UserInput(input.assetId, input.sourceChain, input.amount, [exportAddress], 1, input.destinationChain));
    }
    if (destinationFeeData.amount > BigInt(0)) {
        // adding fees as user input to be able to export it
        fixedUserInputs.push(new input_1.UserInput(destinationFeeData.assetId, userInputs[0].sourceChain, destinationFeeData.amount, [exportAddress], 1, userInputs[0].destinationChain));
    }
    const outputs = (0, builder_1.buildTransactionOutputs)(fixedUserInputs, inputs, sourceFeeData, changeAddress);
    const exportedOutputs = [];
    const changeOutputs = [];
    for (const output of outputs) {
        if (!output.isChange) {
            exportedOutputs.push(output);
        }
        else {
            changeOutputs.push(output);
        }
    }
    return new transaction_2.PlatformExportTransaction(networkId, new types_1.BlockchainId(sourceId), changeOutputs, inputs, memo, new types_1.BlockchainId(destinationId), exportedOutputs);
}
exports.buildPlatformExportTransaction = buildPlatformExportTransaction;
function buildPlatformImportTransaction(userInputs, utxoSet, signersAddresses, fee, changeAddress, networkId, memo = '') {
    if (userInputs.length < 1) {
        throw new utils_1.InputError('user inputs cannot be empty');
    }
    const sourceId = userInputs[0].sourceChain.id;
    const destinationId = userInputs[0].destinationChain.id;
    for (const input of userInputs) {
        if (input.sourceChain.id !== sourceId || input.destinationChain.id !== destinationId) {
            throw new utils_1.InputError('jvm import transaction cannot have different source or destination chain user inputs');
        }
        if (input.sourceChain.id === input.destinationChain.id) {
            throw new utils_1.InputError('jvm import transaction cannot have the same chain as source and destination user inputs');
        }
    }
    const feeData = new transaction_1.TransactionFee(userInputs[0].destinationChain, fee);
    const inputs = [];
    const importedInputs = [];
    for (const input of (0, builder_1.buildTransactionInputs)(userInputs, utxoSet, signersAddresses, [feeData])) {
        if (input.input.utxo === undefined) {
            throw new utils_1.InputError('input cannot use read only utxo');
        }
        const utxo = input.input.utxo;
        if (utxo.sourceChain === undefined) {
            inputs.push(input);
        }
        else {
            importedInputs.push(input);
        }
    }
    const outputs = (0, builder_1.buildTransactionOutputs)(userInputs, inputs.concat(importedInputs), feeData, changeAddress);
    return new transaction_2.PlatformImportTransaction(networkId, new types_1.BlockchainId(destinationId), outputs, inputs, memo, new types_1.BlockchainId(sourceId), importedInputs);
}
exports.buildPlatformImportTransaction = buildPlatformImportTransaction;
function buildAddSupernetValidatorTransaction(utxoSet, signersAddresses, fee, chain, nodeId, stakePeriod, weight, supernetId, supernetAuth, changeAddress, networkId, memo = '') {
    const inputs = (0, builder_1.buildTransactionInputs)([], utxoSet, signersAddresses, [
        new transaction_1.TransactionFee(chain, fee)
    ]);
    const outputs = (0, builder_1.buildTransactionOutputs)([], inputs, new transaction_1.TransactionFee(chain, fee), changeAddress);
    const validator = new supernet_1.Validator(typeof nodeId === 'string' ? new types_1.NodeId(nodeId) : nodeId, stakePeriod, weight);
    return new transaction_2.AddSupernetValidatorTransaction(networkId, new types_1.BlockchainId(chain.id), outputs, inputs, memo, validator, typeof supernetId === 'string' ? new types_1.SupernetId(supernetId) : supernetId, supernetAuth);
}
exports.buildAddSupernetValidatorTransaction = buildAddSupernetValidatorTransaction;
function buildCreateSupernetTransaction(utxoSet, signersAddresses, fee, chain, supernetAuthAddresses, supernetAuthThreshold, changeAddress, networkId, memo = '') {
    const inputs = (0, builder_1.buildTransactionInputs)([], utxoSet, signersAddresses, [
        new transaction_1.TransactionFee(chain, fee)
    ]);
    const outputs = (0, builder_1.buildTransactionOutputs)([], inputs, new transaction_1.TransactionFee(chain, fee), changeAddress);
    const rewardsOwner = new output_1.Secp256k1OutputOwners(BigInt(0), supernetAuthThreshold, types_1.Address.toAddresses(supernetAuthAddresses));
    return new transaction_2.CreateSupernetTransaction(networkId, new types_1.BlockchainId(chain.id), outputs, inputs, memo, rewardsOwner);
}
exports.buildCreateSupernetTransaction = buildCreateSupernetTransaction;
function buildCreateChainTransaction(utxoSet, signersAddresses, fee, chain, supernetId, name, chainAssetId, vmId, fxIds, genesisData, supernetAuth, changeAddress, networkId, memo = '') {
    const inputs = (0, builder_1.buildTransactionInputs)([], utxoSet, signersAddresses, [
        new transaction_1.TransactionFee(chain, fee)
    ]);
    const outputs = (0, builder_1.buildTransactionOutputs)([], inputs, new transaction_1.TransactionFee(chain, fee), changeAddress);
    return new transaction_2.CreateChainTransaction(networkId, new types_1.BlockchainId(chain.id), outputs, inputs, memo, typeof supernetId === 'string' ? new types_1.SupernetId(supernetId) : supernetId, name, typeof chainAssetId === 'string' ? new types_1.AssetId(chainAssetId) : chainAssetId, typeof vmId === 'string' ? new types_1.DynamicId(vmId) : vmId, fxIds, genesisData, supernetAuth);
}
exports.buildCreateChainTransaction = buildCreateChainTransaction;
function buildRemoveSupernetValidatorTransaction(utxoSet, signersAddresses, fee, chain, nodeId, supernetId, supernetAuth, changeAddress, networkId, memo = '') {
    const inputs = (0, builder_1.buildTransactionInputs)([], utxoSet, signersAddresses, [
        new transaction_1.TransactionFee(chain, fee)
    ]);
    const outputs = (0, builder_1.buildTransactionOutputs)([], inputs, new transaction_1.TransactionFee(chain, fee), changeAddress);
    return new transaction_2.RemoveSupernetValidatorTransaction(networkId, new types_1.BlockchainId(chain.id), outputs, inputs, memo, typeof nodeId === 'string' ? new types_1.NodeId(nodeId) : nodeId, typeof supernetId === 'string' ? new types_1.SupernetId(supernetId) : supernetId, supernetAuth);
}
exports.buildRemoveSupernetValidatorTransaction = buildRemoveSupernetValidatorTransaction;
function buildTransferSupernetOwnershipTransaction(utxoSet, signersAddresses, fee, chain, supernetId, supernetAuth, supernetAuthAddresses, supernetAuthThreshold, changeAddress, networkId, memo = '') {
    const inputs = (0, builder_1.buildTransactionInputs)([], utxoSet, signersAddresses, [
        new transaction_1.TransactionFee(chain, fee)
    ]);
    const outputs = (0, builder_1.buildTransactionOutputs)([], inputs, new transaction_1.TransactionFee(chain, fee), changeAddress);
    const owner = new output_1.Secp256k1OutputOwners(BigInt(0), supernetAuthThreshold, types_1.Address.toAddresses(supernetAuthAddresses));
    return new transaction_2.TransferSupernetOwnershipTransaction(networkId, new types_1.BlockchainId(chain.id), outputs, inputs, memo, typeof supernetId === 'string' ? new types_1.SupernetId(supernetId) : supernetId, supernetAuth, owner);
}
exports.buildTransferSupernetOwnershipTransaction = buildTransferSupernetOwnershipTransaction;
function buildTransformSupernetTransaction(utxoSet, signersAddresses, fee, chain, supernetId, assetId, initialRewardPoolSupply, startRewardShare, startRewardTime, targetRewardShare, targetRewardTime, minValidatorStake, maxValidatorStake, minStakeDuration, maxStakeDuration, stakePeriodRewardShare, minDelegationFee, maxDelegationFee, minDelegatorStake, maxValidatorWeightFactor, uptimeRequirement, supernetAuth, changeAddress, networkId, memo = '') {
    // TODO when multisig update [changeAddress] and threshold
    const userInput = new input_1.UserInput(assetId.value, chain, initialRewardPoolSupply, [changeAddress], 1, chain);
    const inputs = (0, builder_1.buildTransactionInputs)([userInput], utxoSet, signersAddresses, [
        new transaction_1.TransactionFee(chain, fee)
    ]);
    const outputs = (0, builder_1.buildTransactionOutputs)([userInput], inputs, new transaction_1.TransactionFee(chain, fee), changeAddress);
    const changeOutputs = [];
    for (const output of outputs) {
        if (output.isChange) {
            changeOutputs.push(output);
        }
    }
    return new transaction_2.TransformSupernetTransaction(networkId, new types_1.BlockchainId(chain.id), changeOutputs, inputs, memo, supernetId, assetId, initialRewardPoolSupply, startRewardShare, startRewardTime, targetRewardShare, targetRewardTime, minValidatorStake, maxValidatorStake, minStakeDuration, maxStakeDuration, stakePeriodRewardShare, minDelegationFee, maxDelegationFee, minDelegatorStake, maxValidatorWeightFactor, uptimeRequirement, supernetAuth);
}
exports.buildTransformSupernetTransaction = buildTransformSupernetTransaction;
function buildAddPermissionlessValidatorTransaction(utxoSet, signersAddresses, fee, chain, nodeId, stakePeriod, supernetId, stakeAmount, stakedAssetId, shares, signer, stakeAddresses, stakeThreshold, stakeLocktime, rewardAddresses, rewardThreshold, rewardLocktime, changeAddress, networkId, memo = '') {
    const stakeData = new input_1.UserInput(stakedAssetId, chain, stakeAmount, stakeAddresses, stakeThreshold, chain, stakeLocktime);
    // sort set by locktime so stakeable locked are processed first
    // non stakeable utxos should not be present there as it would
    // throw an error during outputs building, we assume that the set
    // was pre sorted in order not to have those
    utxoSet.sort((a, b) => {
        return Number(b.output.locktime - a.output.locktime);
    });
    const inputs = (0, builder_1.buildTransactionInputs)([stakeData], utxoSet, signersAddresses, [
        new transaction_1.TransactionFee(chain, fee)
    ]);
    const validator = new supernet_1.Validator(typeof nodeId === 'string' ? new types_1.NodeId(nodeId) : nodeId, stakePeriod, stakeAmount);
    const stake = (0, builder_1.buildStakeOutputs)(stakeData, inputs);
    const rewardsOwner = new output_1.Secp256k1OutputOwners(rewardLocktime, rewardThreshold, types_1.Address.toAddresses(rewardAddresses));
    // same as for utxo set sorting because inputs are used to build the change outputs
    // also using locktime but in inverted order so change prioritizes lowest locktime
    // first and if mixed with unlocked utxos change will then be unlocked instead of
    // locked stakeable change output. Also note that here we did sorting after building
    // of the stake, because the stake must use the same order of inputs as the utxoSet
    // they were build from
    inputs.sort((a, b) => {
        return Number(a.input.utxo.output.locktime - b.input.utxo.output.locktime);
    });
    const outputs = (0, builder_1.buildTransactionOutputs)([stakeData], inputs, new transaction_1.TransactionFee(chain, fee), changeAddress);
    const changeOutputs = [];
    for (const output of outputs) {
        if (output.isChange) {
            changeOutputs.push(output);
        }
    }
    return new transaction_2.AddPermissionlessValidatorTransaction(networkId, new types_1.BlockchainId(chain.id), changeOutputs, // only using change outputs because of stake
    inputs, memo, validator, typeof supernetId === 'string' ? new types_1.SupernetId(supernetId) : supernetId, signer, stake, rewardsOwner, rewardsOwner, shares);
}
exports.buildAddPermissionlessValidatorTransaction = buildAddPermissionlessValidatorTransaction;
function buildAddPermissionlessDelegatorTransaction(utxoSet, signersAddresses, fee, chain, nodeId, stakePeriod, supernetId, stakeAmount, stakedAssetId, stakeAddresses, stakeThreshold, stakeLocktime, rewardAddresses, rewardThreshold, rewardLocktime, changeAddress, networkId, memo = '') {
    const stakeData = new input_1.UserInput(stakedAssetId, chain, stakeAmount, stakeAddresses, stakeThreshold, chain, stakeLocktime);
    utxoSet.sort((a, b) => {
        return Number(b.output.locktime - a.output.locktime);
    });
    const inputs = (0, builder_1.buildTransactionInputs)([stakeData], utxoSet, signersAddresses, [
        new transaction_1.TransactionFee(chain, fee)
    ]);
    const validator = new supernet_1.Validator(typeof nodeId === 'string' ? new types_1.NodeId(nodeId) : nodeId, stakePeriod, stakeAmount);
    const stake = (0, builder_1.buildStakeOutputs)(stakeData, inputs);
    const rewardsOwner = new output_1.Secp256k1OutputOwners(rewardLocktime, rewardThreshold, types_1.Address.toAddresses(rewardAddresses));
    inputs.sort((a, b) => {
        return Number(a.input.utxo.output.locktime - b.input.utxo.output.locktime);
    });
    const outputs = (0, builder_1.buildTransactionOutputs)([stakeData], inputs, new transaction_1.TransactionFee(chain, fee), changeAddress);
    const changeOutputs = [];
    for (const output of outputs) {
        if (output.isChange) {
            changeOutputs.push(output);
        }
    }
    return new transaction_2.AddPermissionlessDelegatorTransaction(networkId, new types_1.BlockchainId(chain.id), changeOutputs, // only using change outputs because of stake
    inputs, memo, validator, typeof supernetId === 'string' ? new types_1.SupernetId(supernetId) : supernetId, stake, rewardsOwner);
}
exports.buildAddPermissionlessDelegatorTransaction = buildAddPermissionlessDelegatorTransaction;
